import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Shield, Lock } from 'lucide-react';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import type { UpgradeModalVariant } from '../../contexts/UpgradeModalContext';
import { useCheckout } from '../../hooks/useCheckout';
import { PLAN_PRICES_USD, formatUsd, type CheckoutPlanId } from '../../data/pricingPlans';
import { PricingPlans } from '../Pricing/PricingPlans';
import { CheckoutAuthPrompt } from '../Pricing/CheckoutAuthPrompt';
interface PromptCopy {
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  primaryAction: 'free_scan' | 'single_scan' | 'invite';
}
const PROMPTS: Record<Exclude<UpgradeModalVariant, 'plans'>, PromptCopy> = {
  welcome: {
    eyebrow: 'Access granted',
    title: 'Welcome to ToxicDetector AI 🛡️',
    body: 'Start with your free forensic scan or unlock unlimited analysis.',
    primaryLabel: 'Start free scan',
    primaryAction: 'free_scan'
  },
  free_scan_complete: {
    eyebrow: 'Scan complete',
    title: 'Want deeper analysis?',
    body: `Your free scan is complete. Unlock the full forensic report for ${formatUsd(PLAN_PRICES_USD.single_scan)} or get unlimited access with a subscription.`,
    primaryLabel: `Get full report — ${formatUsd(PLAN_PRICES_USD.single_scan)}`,
    primaryAction: 'single_scan'
  },
  no_credits: {
    eyebrow: 'No credits left',
    title: "You're out of free scans",
    body: 'Share ToxicDetector with a friend and earn 5 free credits, or unlock unlimited analysis.',
    primaryLabel: 'Invite a friend',
    primaryAction: 'invite'
  }
};
export function UpgradeModal() {
  const {
    variant,
    isOpen,
    closeUpgradeModal
  } = useUpgradeModal();
  const navigate = useNavigate();
  const {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt
  } = useCheckout('upgrade_modal');
  const [showPlans, setShowPlans] = useState(false);
  useEffect(() => {
    setShowPlans(variant === 'plans');
  }, [variant]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeUpgradeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, closeUpgradeModal]);
  const prompt = variant && variant !== 'plans' ? PROMPTS[variant] : PROMPTS.welcome;
  const goTo = (path: string) => {
    closeUpgradeModal();
    navigate(path);
  };
  const handlePrimary = () => {
    if (prompt.primaryAction === 'free_scan') return goTo('/upload');
    if (prompt.primaryAction === 'invite') return goTo('/dashboard/referrals');
    void onCheckout('single_scan');
  };
  const handleCheckout = (plan: CheckoutPlanId) => {
    void onCheckout(plan);
  };
  return <AnimatePresence data-id="element-3120" data-mp="hhitpdjhqzb3">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-3121" data-mp="mhexbboa0oq4">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={closeUpgradeModal} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-3122" data-mp="rgi16ssisowi" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className={`relative z-10 max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-gray-800 bg-[#050505] p-6 sm:rounded-2xl sm:p-8 ${showPlans ? 'max-w-5xl' : 'max-w-lg'}`} data-id="element-3123" data-mp="8p8ibswv0ns9">
            <button type="button" onClick={closeUpgradeModal} aria-label="Close" className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-3124" data-mp="25fjubgdepz7v">
              <X size={18} data-id="element-3125" data-mp="2afpfqet32phi" />
            </button>

            {showPlans ? <div data-id="element-3126" data-mp="l1ava6p3lort">
                <header className="mb-7 max-w-xl pr-10" data-id="element-3127" data-mp="l0rmbfhfeapx">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF0000]" data-id="element-3128" data-mp="g1ogvtxja2hf">
                    Clearance levels
                  </p>
                  <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-3129" data-mp="2rql8wsrztvkx">
                    Choose your access
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400" data-id="element-3130" data-mp="2mxa38uyx3kul">
                    Every plan runs the same forensic engine — pick how much
                    clarity you need.
                  </p>
                </header>
                <PricingPlans onCheckout={handleCheckout} pendingPlan={pendingPlan} freeHref="/upload" data-id="element-3131" data-mp="2dyokaypja3ta" />
              </div> : <div data-id="element-3132" data-mp="2410dhaenl5ur">
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#FF0000]/30 bg-[#FF0000]/10" data-id="element-3133" data-mp="3tdueop1rya1w">
                  <Shield className="h-5 w-5 text-[#FF0000]" data-id="element-3134" data-mp="26hx48wzbulg" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500" data-id="element-3135" data-mp="qnlutqo75tw">
                  {prompt.eyebrow}
                </p>
                <h2 id="upgrade-modal-title" className="mt-2 text-2xl font-black uppercase tracking-tight text-white" data-id="element-3136" data-mp="3ryrp9sz9g4nf">
                  {prompt.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-gray-400" data-id="element-3137" data-mp="jgltj8qk2wx0">
                  {prompt.body}
                </p>

                <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-3138" data-mp="ehipnrmbawqm">
                  <button type="button" autoFocus onClick={handlePrimary} disabled={pendingPlan !== null} className="flex-1 rounded-xl bg-[#FF0000] px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-3139" data-mp="9qtvg4j8zlns">
                    {pendingPlan ? 'Processing…' : prompt.primaryLabel}
                  </button>
                  <button type="button" onClick={() => setShowPlans(true)} className="flex-1 rounded-xl border border-gray-700 bg-transparent px-5 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-3140" data-mp="2p3b50njjfhrf">
                    View plans
                  </button>
                </div>

                <p className="mt-5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-600" data-id="element-3141" data-mp="3csvor8nimj1r">
                  <Lock size={10} aria-hidden="true" data-id="element-3142" data-mp="3hqxak1cbswgx" /> Secure checkout · Cancel
                  anytime
                </p>
              </div>}
          </motion.div>

          <CheckoutAuthPrompt plan={authPromptPlan} onClose={dismissAuthPrompt} data-id="element-3143" data-mp="2xmutfxwgnol7" />
        </div>}
    </AnimatePresence>;
}